import React from "react";
import { Container } from "react-bootstrap";
import { Link } from "react-router-dom";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import { getPageName } from "../../../locales/translationUtils";
import "./ListView.css";

interface Aposta {
  titulo: string;
  corpo: React.ReactNode;
  pitch: string;
  fontes: string[];
  rota: string;
}

const apostas: Aposta[] = [
  {
    titulo: "Torre de controle preditiva de atraso",
    rota: "torre-controle",
    corpo: (
      <>
        Hoje o sistema é <strong>reativo</strong>: alguém precisa abrir a tela pra saber se uma carga está atrasando.
        Com o histórico de armazém de origem, modal e transportador já registrado, dá pra calcular o lead time
        esperado por rota e sinalizar embarques se desviando do padrão — antes do cliente ligar perguntando.
      </>
    ),
    pitch: "Para contas AOG-sensíveis (peça aeronáutica parada = avião parado), isso é diferencial competitivo, não eficiência interna.",
    fontes: ["Cargo.ArrivalDate", "Release.ShipDate/Released", "CarrierInformation"],
  },
  {
    titulo: "Portal do cliente em tempo real",
    rota: "portal-cliente",
    corpo: (
      <>
        O cliente hoje recebe um <strong>e-mail diário</strong> com o status. O papel ROLE_CLIENT já existe no
        sistema — falta só abrir telas de consulta ao vivo (status de carga, pendência documental, histórico) em
        vez de esperar o próximo relatório.
      </>
    ),
    pitch: "Reduz o \"cadê minha carga\" por telefone, e é o tipo de item que pesa numa cotação/RFP de logística.",
    fontes: ["ROLE_CLIENT", "WareHouseClient", "EmailLog (RelatorioDiario)"],
  },
  {
    titulo: "Radar de compliance documental e DGR",
    rota: "compliance-dgr",
    corpo: (
      <>
        "Aceitar sem original" e "discrepância documental" já são campos no banco — hoje são só uma caixinha
        marcada no formulário, sem trilha de auditoria nem alerta. Somando o sinalizador de mercadoria perigosa
        por carga, dá pra montar um radar de risco que hoje <strong>não existe visualmente para ninguém</strong>,
        nem para o master.
      </>
    ),
    pitch: "Documentação inconsistente é o tipo de coisa que vira multa ou embargo em fiscalização aduaneira.",
    fontes: ["Release.AgreeDocsWithoutOriginal", "Release.AgreeDocDiscrepancy", "Cargo.Dgr/DgrGoods"],
  },
  {
    titulo: "Otimização de frete: peso taxável e consolidação",
    rota: "otimizacao-frete",
    corpo: (
      <>
        Toda carga tem dimensões (comprimento × largura × altura) <strong>e</strong> peso real lançados
        separadamente — isso permite calcular peso volumétrico (o que o transportador realmente cobra) contra o
        peso real, embarque a embarque, e cruzar com o custo já lançado por carga para achar consolidação.
      </>
    ),
    pitch: "Isso é economia mensurável em reais por embarque — o painel executivo já mostra a média hoje (KPI 03).",
    fontes: ["Dimension (L×W×H×Qtd)", "Cargo.Weight", "Cost.Value"],
  },
  {
    titulo: "Score de saúde do cliente",
    rota: "score-cliente",
    corpo: (
      <>
        Cruzando taxa de pendência documental por cliente, lead time de liberação por cliente, e taxa de sucesso
        de e-mail, dá pra montar um score simples que aponta qual cliente está tendo experiência ruim{" "}
        <strong>antes</strong> dele simplesmente parar de mandar carga.
      </>
    ),
    pitch: "Vira uma frase de reunião de diretoria: \"identificamos risco de saída da conta X três meses antes.\"",
    fontes: ["Release por Cliente", "EmailLog.Sucesso", "Aging de liberação"],
  },
];

/**
 * As 5 apostas estratégicas propostas para o comércio exterior da Giant Cargo,
 * lidas diretamente do modelo de dados do GiantLog. Protótipo de apresentação:
 * telas navegáveis, sem lógica de negócio nova por trás ainda. Se aprovado, cada
 * aposta vira feature real (endpoints, regras de negócio), seguindo o mesmo padrão
 * usado no Painel Executivo (que já é 100% real).
 */
const IniciativasEstrategicasListView: React.FC = () => {
  const navItems: NavBarItem[] = [
    { label: "Início", route: "/", iconName: "house-door" },
    { label: "Painel Executivo", route: "/painel-executivo", iconName: "speedometer2" },
  ];

  return (
    <Container fluid>
      <NavBar items={navItems} />
      <div className="mb-2"></div>

      <div className="iniciativas-wrap">
        <div className="iniciativas-header">
          <div className="iniciativas-badge">🧭 Protótipo — proposta para aprovação</div>
          <h2>{getPageName("iniciativasEstrategicas")}</h2>
          <p className="iniciativas-subtitle">
            Cinco apostas estratégicas para o comércio exterior da Giant Cargo, lidas direto do modelo de dados
            do GiantLog — não de benchmark genérico de mercado.
          </p>
        </div>

        <div className="iniciativas-context">
          <span className="iniciativas-chip"><b>Origem:</b> armazéns EUA/Canadá</span>
          <span className="iniciativas-chip"><b>Destino:</b> terminais alfandegados no Brasil</span>
          <span className="iniciativas-chip"><b>Perfil:</b> peças industriais/aeroespaciais, alto valor</span>
        </div>

        {apostas.map((a, i) => (
          <div className="bet-card" key={a.titulo}>
            <div className="bet-card-num">{String(i + 1).padStart(2, "0")}</div>
            <div>
              <div className="bet-card-title">{a.titulo}</div>
              <div className="bet-card-body">{a.corpo}</div>
              <div className="bet-card-pitch">{a.pitch}</div>
              <div className="bet-card-sources">
                {a.fontes.map((f) => (
                  <span className="bet-src-tag" key={f}>{f}</span>
                ))}
              </div>
              <Link to={a.rota} className="bet-card-link">Ver protótipo da tela →</Link>
            </div>
          </div>
        ))}

        <div className="iniciativas-footer-note">
          Ponto de partida honesto: os itens de custo, consolidação e score de cliente ficam bem mais fortes com
          dado de receita/contrato, que hoje não existe no GiantLog — vale como próxima conversa. Os 10 KPIs que
          já embasam essas apostas estão calculados de verdade, com dado real, no{" "}
          <Link to="/painel-executivo">Painel Executivo</Link>.
        </div>
      </div>
    </Container>
  );
};

export default IniciativasEstrategicasListView;
